/**
 * Plan management.
 *
 * Prices and limits are rows, so they change without a deploy. What is edited
 * here reaches everybody on the plan immediately — which is the right
 * behaviour for a limit, and the reason the plan *code* is not editable: the
 * identity has to stay stable while the terms move.
 *
 * The Stripe price id is the field that actually matters day to day. Until one
 * is set, a plan cannot be bought — the dashboard shows no button rather than
 * one that 400s — and the ids differ between test and live mode, which is why
 * they are entered here rather than seeded by a migration.
 */

import { useState } from "react";

import { useAdminPlans, useUpdatePlan } from "../../api/queries";
import type { AdminPlan } from "../../api/types";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
} from "../../ui/primitives";

export function AdminPlansPage() {
  const plans = useAdminPlans();
  const [editing, setEditing] = useState<AdminPlan | null>(null);

  if (plans.isLoading) return <Loading />;
  if (plans.error) return <ErrorNote error={plans.error} />;

  return (
    <div className="space-y-4">
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-muted">
              <th scope="col" className="py-2 font-medium">Plan</th>
              <th scope="col" className="font-medium">Price</th>
              <th scope="col" className="font-medium">Conversations</th>
              <th scope="col" className="font-medium">Messages</th>
              <th scope="col" className="font-medium">Sites</th>
              <th scope="col" className="font-medium">Products</th>
              <th scope="col" className="font-medium">Syncs</th>
              <th scope="col" className="font-medium">Stripe price</th>
              <th scope="col" className="font-medium">On it</th>
              <th scope="col" className="sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {(plans.data ?? []).map((plan) => (
              <tr key={plan.id}>
                <td className="py-2">
                  <div className="font-medium">{plan.name}</div>
                  <div className="text-xs text-muted">{plan.code}</div>
                </td>
                <td className="tabular-nums">
                  {plan.price_cents === 0
                    ? "Free"
                    : `$${(plan.price_cents / 100).toFixed(2)}`}
                </td>
                <td className="tabular-nums">
                  {plan.conversation_limit < 0
                    ? "Unlimited"
                    : plan.conversation_limit.toLocaleString()}
                </td>
                <td className="tabular-nums">
                  {plan.message_limit < 0
                    ? "Unlimited"
                    : plan.message_limit.toLocaleString()}
                </td>
                <td className="tabular-nums">
                  {plan.site_limit < 0 ? "Unlimited" : plan.site_limit}
                </td>
                <td className="tabular-nums">
                  {plan.product_limit < 0 ? "Unlimited" : plan.product_limit.toLocaleString()}
                </td>
                <td className="tabular-nums">
                  {plan.sync_limit < 0 ? "Unlimited" : plan.sync_limit.toLocaleString()}
                </td>
                <td>
                  {plan.stripe_price_id ? (
                    <code className="text-xs">{plan.stripe_price_id}</code>
                  ) : (
                    // Not an error — a deployment without Stripe is a
                    // supported state — but it is the reason a plan has no
                    // buy button, and that is worth saying here.
                    <Badge tone="warn">Not purchasable</Badge>
                  )}
                </td>
                <td className="tabular-nums">{plan.subscribers}</td>
                <td className="py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(plan)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-muted">
        Changes apply to everyone already on the plan, immediately.
      </p>

      <EditDialog plan={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function EditDialog({ plan, onClose }: { plan: AdminPlan | null; onClose: () => void }) {
  const update = useUpdatePlan();
  const [draft, setDraft] = useState<Partial<AdminPlan>>({});

  // Reset when a different plan is opened, so an edit to one cannot leak into
  // the next.
  const key = plan?.id ?? "none";

  function field<K extends keyof AdminPlan>(name: K): AdminPlan[K] | undefined {
    return (draft[name] ?? plan?.[name]) as AdminPlan[K] | undefined;
  }

  return (
    <Modal
      key={key}
      open={plan !== null}
      onClose={() => {
        setDraft({});
        onClose();
      }}
      title={plan ? `Edit ${plan.name}` : "Edit plan"}
    >
      {plan && (
        <form
          className="space-y-3"
          onSubmit={async (event) => {
            event.preventDefault();
            await update.mutateAsync({ planId: plan.id, patch: draft });
            setDraft({});
            onClose();
          }}
        >
          {update.error != null && <ErrorNote error={update.error} />}

          <Field label="Display name" id="plan-name">
            <Input
              id="plan-name"
              value={String(field("name") ?? "")}
              onChange={(event) => setDraft((d) => ({ ...d, name: event.target.value }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Price (cents)" id="plan-price">
              <Input
                id="plan-price"
                type="number"
                min={0}
                value={String(field("price_cents") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, price_cents: Number(event.target.value) }))
                }
              />
            </Field>

            <Field label="Conversations" id="plan-conversations" hint="-1 for unlimited.">
              <Input
                id="plan-conversations"
                type="number"
                min={-1}
                value={String(field("conversation_limit") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, conversation_limit: Number(event.target.value) }))
                }
              />
            </Field>

            <Field
              label="Messages"
              id="plan-messages"
              hint="-1 for unlimited. Each message is one LLM call."
            >
              <Input
                id="plan-messages"
                type="number"
                min={-1}
                value={String(field("message_limit") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, message_limit: Number(event.target.value) }))
                }
              />
            </Field>

            <Field label="Sites" id="plan-sites" hint="-1 for unlimited.">
              <Input
                id="plan-sites"
                type="number"
                min={-1}
                value={String(field("site_limit") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, site_limit: Number(event.target.value) }))
                }
              />
            </Field>

            <Field label="Products per store" id="plan-products" hint="-1 for unlimited.">
              <Input
                id="plan-products"
                type="number"
                min={-1}
                value={String(field("product_limit") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, product_limit: Number(event.target.value) }))
                }
              />
            </Field>

            <Field
              label="Syncs per period"
              id="plan-syncs"
              hint="Each press of Sync now. -1 for unlimited."
            >
              <Input
                id="plan-syncs"
                type="number"
                min={-1}
                value={String(field("sync_limit") ?? 0)}
                onChange={(event) =>
                  setDraft((d) => ({ ...d, sync_limit: Number(event.target.value) }))
                }
              />
            </Field>
          </div>

          <Field
            label="Stripe price ID"
            id="plan-stripe"
            hint="From the Stripe dashboard. Test and live mode have different ids; until one is set the plan cannot be bought."
          >
            <Input
              id="plan-stripe"
              placeholder="price_1A2b3C..."
              value={String(field("stripe_price_id") ?? "")}
              onChange={(event) =>
                setDraft((d) => ({ ...d, stripe_price_id: event.target.value }))
              }
            />
          </Field>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              onClick={() => {
                setDraft({});
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
